import { useState } from "react";

import { ChevronDown } from "lucide-react";

import { Button, DialogHeader, Field, InfoTooltip, Modal } from "~/components";
import { Form, RadioGroup, RadioWithContentGroupItem, useForm, z } from "~/components/form";
import { QuestionnaireSubheading } from "~/components/questionnaire/components";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "~/components/ui/accordion";
import { getPackagePrice } from "~/lib/cart";
import { cn, currency } from "~/lib/utils";
import { PackageSessionData } from "~/models/package_session.server";

import { ServiceList, ServiceListItem } from "../ServiceList";
import { Footer, ScreenFormActions } from "./Footer";
import QuestionHeading from "./QuestionHeading";
import { PackageScreenProps } from "./_route";

export const INTENT = "package-tier";

export const schema = z.object({
    intent: z.literal(INTENT),
    packageTier: z.string({ required_error: "Tier selection required" }),
});

const VALUES = {
    premium: {
        value: "premium",
        label: "Premium",
    },
    standard: {
        value: "standard",
        label: "Standard",
    },
    basic: {
        value: "basic",
        label: "Basic",
    },
};

const ORDERED_VALUES = [VALUES.premium, VALUES.standard, VALUES.basic];

export function getValue(packageOptions: PackageSessionData) {
    return VALUES[packageOptions.packageTier].label;
}

export function Screen({ currentPackage, packageOptions }: PackageScreenProps) {
    const [form] = useForm(schema, {
        shouldValidate: "onInput",
        defaultValue: {
            packageTier: packageOptions.packageTier,
        },
    });

    return (
        <section className="flex flex-col justify-center gap-10">
            <section className="flex flex-col gap-4">
                <QuestionHeading>Select Your Service Level</QuestionHeading>
                <QuestionnaireSubheading>Please select an option to continue</QuestionnaireSubheading>
            </section>
            <Form form={form} method="post" className="flex flex-col space-y-8">
                <Field>
                    <RadioGroup name="packageTier" className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                        {ORDERED_VALUES.map(({ value, label }) => (
                            <RadioWithContentGroupItem
                                variant="content"
                                key={value}
                                value={value}
                                title={label}
                                description={<PackageTier tier={value} cremationPackage={currentPackage} />}
                                aria-label={label}
                            />
                        ))}
                    </RadioGroup>
                    {/* FIX: Opening modal triggers error message - don't need this for now as continue disabled */}
                    {/* <div className="min-h-5">
                        <FieldError name="packageTier" />
                    </div> */}
                </Field>
                <Footer>
                    <ScreenFormActions disabled={!form.value} intent={INTENT} />
                </Footer>
            </Form>
        </section>
    );
}

function PackageTier({ tier, cremationPackage }) {
    const [isOpen, setIsOpen] = useState(false);

    const services = cremationPackage.services
        .filter((service) => service.type !== "discount" && (!service.tier || service.tier === tier))
        .sort((a, z) => {
            return a.tier - z.tier;
        });
    const discounts = cremationPackage.services.filter(
        (service) => service.type === "discount" && (!service.tier || service.tier === tier),
    );

    const { subtotal, preDiscount } = getPackagePrice(cremationPackage, { packageTier: tier });

    const commonServices = services.filter(({ tier }) => !tier);

    return (
        <div className="flex flex-col gap-4">
            This type of package includes:
            <Accordion type="single" collapsible>
                <AccordionItem value="item-1" className="border-t">
                    <AccordionTrigger className="w-full [&>li]:flex-1 [&>svg]:hidden [&[data-state=open]>li_svg]:-rotate-180">
                        <ServiceListItem>
                            <div className="flex justify-between items-center grow">
                                <div className="flex flex-wrap gap-2">
                                    Common services
                                    <div className="text-sm underline font-semibold hover:underline mt-0.5 flex items-center gap-1">
                                        What's Included
                                        <ChevronDown className="w-4 h-4 transition-transform duration-300" />
                                    </div>
                                </div>
                                <div>
                                    {currency(
                                        commonServices
                                            .filter(({ price }) => price)
                                            .reduce((prev, curr) => prev + curr.price, 0),
                                    )}
                                </div>
                            </div>
                        </ServiceListItem>
                    </AccordionTrigger>
                    <AccordionContent>
                        <ServiceList className="py-1 pt-6 border-t relative before:absolute before:inset-[-8px] before:rounded-sm before:bg-secondary/0">
                            {commonServices.map((service, index) => (
                                <ServiceListItem key={index}>
                                    <div className="flex justify-between grow">
                                        <div>{service.name}</div>
                                        <div>{currency(service.price)}</div>
                                    </div>
                                </ServiceListItem>
                            ))}
                        </ServiceList>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
            <ServiceList>
                {services
                    .filter(({ tier }) => !!tier)
                    .map((service, index) => (
                        <ServiceListItem key={index} className={cn((service.product || service.image) && "order-12")}>
                            <div className="flex flex-col grow">
                                <div className="flex justify-between grow">
                                    <div className="flex gap-2 items-center">
                                        {service.name}
                                        {service.tooltip ? <InfoTooltip content={service.tooltip} /> : null}
                                    </div>

                                    <div>{currency(service.price)}</div>
                                </div>
                                {service.product || service.image ? (
                                    <>
                                        <div className="mt-4">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium">{service.product}</p>
                                                <p className="">{service.subtitle}</p>
                                            </div>
                                            <Button
                                                className="capitalize underline"
                                                variant="action"
                                                onClick={() => setIsOpen(true)}
                                            >
                                                View Details
                                            </Button>
                                            {service.image ? (
                                                <img
                                                    className="py-2 h-[200px] object-contain mx-auto"
                                                    src={service.image}
                                                    alt={service.product}
                                                />
                                            ) : null}
                                        </div>
                                        <Modal
                                            className="max-w-2xl lg:max-w-5xl lg:min-h-[200px] md:px-12 md:pt-12 md:pb-16"
                                            open={isOpen}
                                            onOpenChange={() => setIsOpen(false)}
                                            triggerButton={false}
                                        >
                                            <DialogHeader className="flex flex-col gap-8">
                                                <section className="flex flex-col lg:flex-row flex-1 gap-8 md:gap-16 items-center">
                                                    <img
                                                        className="object-contain w-full max-w-[440px]"
                                                        src={service.image}
                                                        alt={service.product}
                                                    />
                                                    <section className="flex flex-col gap-6 text-left">
                                                        <h2 className="font-title font-semibold text-xl md:text-3xl !leading-relaxed">
                                                            {service.product}
                                                        </h2>
                                                        <p className="font-normal text-lg text-foreground/50">
                                                            Description:
                                                        </p>
                                                        {/* TODO: Add real descriptions */}
                                                        <p className="text-lg">{service.description}</p>
                                                    </section>
                                                </section>
                                            </DialogHeader>
                                        </Modal>
                                    </>
                                ) : null}
                            </div>
                        </ServiceListItem>
                    ))}
            </ServiceList>
            <ul className="border-t border-border pt-6 pb-2 flex flex-col gap-3 mt-auto">
                <li className="flex justify-between">
                    <div>Total Plan Price</div>
                    <div className={cn(discounts.length === 0 ? "font-text font-bold" : null)}>
                        {currency(preDiscount)}
                    </div>
                </li>
                {discounts.map(({ field, discount, name }) => (
                    <li className="flex justify-between" key={field}>
                        <div>{name}</div>
                        <div>{currency(discount)}</div>
                    </li>
                ))}
                {discounts.length > 0 ? (
                    <li className="flex justify-between">
                        <div>Plan Price with Savings (*)</div>
                        <div className="font-text font-bold">{currency(subtotal)}</div>
                    </li>
                ) : null}
            </ul>
        </div>
    );
}
